import { ReactNode, useMemo } from 'react';

import { Message } from '@/components/ui/message';
import { useNotifications } from '@/components/ui/notifications';
import { GlyTouCanError } from '@/types/error';

const isSchemaValidationError = (error: unknown): error is GlyTouCanError =>
  error instanceof GlyTouCanError &&
  error.errorType === 'SCHEMA_VALIDATION_ERROR';

const isInvalidInputError = (error: unknown): error is GlyTouCanError =>
  error instanceof GlyTouCanError && error.errorType === 'INVALID_INPUT_ERROR';

const isAuthorizationError = (error: unknown): error is GlyTouCanError =>
  error instanceof GlyTouCanError && error.errorType === 'AUTHORIZATION_ERROR';

const isSearchNotFoundError = (error: unknown): error is GlyTouCanError =>
  error instanceof GlyTouCanError &&
  error.errorType === 'SEARCH_NOT_FOUND_ERROR';

const isHttpError = (error: unknown): error is GlyTouCanError =>
  error instanceof GlyTouCanError && error.errorType === 'HTTP_ERROR';

const isRequestUnreachableError = (error: unknown): error is GlyTouCanError =>
  error instanceof GlyTouCanError &&
  error.errorType === 'REQUEST_UNREACHABLE_ERROR';

const isOtherError = (error: unknown): error is Error => error instanceof Error;

export const useErrorReporter = () => {
  const { addNotification } = useNotifications();

  return useMemo(
    () => ({
      report: (error: unknown, duration?: number | 'infinite') => {
        let title: string = '';
        let description: ReactNode = <></>;
        let url: string | undefined = undefined;

        if (isSchemaValidationError(error)) {
          title = 'Schema validation error';
          description = <span>{error.message}</span>;
          url = error.url;
        } else if (isInvalidInputError(error)) {
          title = 'Invalid input error';
          description = <Message code={error.message} withCode />;
          url = error.url;
        } else if (isAuthorizationError(error)) {
          title = 'Authorization error';
          description = <span>{error.message}</span>;
          url = error.url;
        } else if (isSearchNotFoundError(error)) {
          title = 'Not found error';
          description = error.query ? (
            <span>{error.query} does not exist</span>
          ) : (
            <Message code={error.message} withCode />
          );
          url = error.url;
        } else if (isHttpError(error)) {
          title = 'HTTP error';
          description = <span>{error.message}</span>;
          url = error.url;
        } else if (isRequestUnreachableError(error)) {
          title = 'Request unreachable error';
          description = <span>{error.message}</span>;
          url = error.url;
        } else if (isOtherError(error)) {
          title = 'Error';
          description = <span>{error.message}</span>;
        } else {
          title = 'Unknown error';
          description = <span>Unknown error occurred.</span>;
        }

        console.error(error);

        addNotification({
          type: 'error',
          title: title,
          timestamp: new Date(),
          description: {
            body: description,
            url: url,
          },
          duration: duration ?? 5000,
        });
      },
    }),
    [addNotification],
  );
};
